import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Contact } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save contact data.
 */
@Injectable()
export class ContactService {
    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all contacts for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null): Observable<Contact[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const contacts: Contact[] = [];
                const bodyData: any = response.data;
                for (const contact of bodyData) {
                    contacts.push((new Contact()).deserialize(contact));
                }
                return contacts;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(eventId: string, contactId: string, includes: string[] = null): Observable<Contact> {
        const apiRequestUrl: string = this.getApiUrl(eventId, contactId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Contact().deserialize(response))));
    }

    /**
     * Add or update the contact
     */
    public save(eventId: string, contact: Contact, includes: string[] = null): Observable<Contact> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(eventId, contact.id, includes);

        if (contact.id) {
            // Update existing Contact
            response = this.http.patch(apiRequestUrl, contact.serialize());
        } else {
            // Add a new Contact
            response = this.http.post(apiRequestUrl, contact.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new Contact()).deserialize(response)));
    }

    /**
     * Delete the specified contact.
     */
    public delete(eventId: string, contactId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, contactId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, contactId: string, includes: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/contacts/`;

        if (contactId) {
            url += contactId;
        }

        url += this.http.getQueryParameters(includes);

        return url;
    }
}
